import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useState,
} from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import SimpleTooltip from "./SimpleToolTip";
import ColumnMenu from "./ColumnMenu";
import * as XLSX from "xlsx";

const VirtualizedTable = forwardRef(
  (
    {
      data,
      columns,
      height = 500,
      showCheckbox = true,
      hiddenColumns = [],
      initialSelectedRows = [],
      getRowStyle = () => ({}),
      onNotificationClick,
      showRecordCount = true,
      paddingBackgroundTheme = 0,
      showExportButton = false,
      exportFileName = "ExportedData",
    },
    ref
  ) => {
    const [columnVisibility, setColumnVisibility] = useState({});
    const [columnSizing, setColumnSizing]         = useState({});

    const enhancedColumns = showCheckbox
      ? [
          {
            id: "select",
            enableResizing: false,
            header: ({ table }) => (
              <input
                type="checkbox"
                checked={table.getIsAllPageRowsSelected?.()}
                onChange={table.getToggleAllPageRowsSelectedHandler?.()}
              />
            ),
            cell: ({ row }) => (
              <input
                type="checkbox"
                checked={row.getIsSelected?.()}
                disabled={!row.getCanSelect?.()}
                onChange={row.getToggleSelectedHandler?.()}
              />
            ),
            size: 40,
          },
          ...columns,
        ]
      : columns;

    const visibleColumns = enhancedColumns.filter(
      (col) => !hiddenColumns.includes(col.accessorKey ?? col.id)
    );

    const table = useReactTable({
      data,
      columns: visibleColumns,
      state: { columnVisibility, columnSizing },
      onColumnVisibilityChange: setColumnVisibility,
      onColumnSizingChange: setColumnSizing,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      enableRowSelection: showCheckbox,
      columnResizeMode: "onChange",
      getRowId: (row) => {
        const tripNo = row.TripNo;
        return tripNo != null ? tripNo.toString() : `row-${row.index}`;
      },
    });

    useEffect(() => {
      if (initialSelectedRows && table) {
        table.setRowSelection(initialSelectedRows);
      }
    }, [initialSelectedRows, table]);

    useImperativeHandle(ref, () => ({
      getSelectedRows: () =>
        table.getSelectedRowModel().rows.map((r) => r.original),
    }));

    const parentRef = useRef(null);

    const rowVirtualizer = useVirtualizer({
      count: table.getRowModel().rows.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => 40,
      overscan: 10,
    });

    const gridTemplateColumns = table
      .getAllLeafColumns()
      .map((col) => `${col.getSize()}px`)
      .join(" ");
    const totalTableWidth = table.getTotalSize() + paddingBackgroundTheme;

    // ── Pointer-capture resize ────────────────────────────────────────────
    // All pointer events are locked to the handle element for the drag.
    // No document listeners → no stacking, no leaks, works in modals.
    const handleResizePointerDown = (e, header) => {
      e.preventDefault();
      e.stopPropagation();

      const el        = e.currentTarget;
      const pointerId = e.pointerId;
      const startX    = e.clientX;
      const startSize = header.column.getSize();
      const columnId  = header.column.id;

      el.setPointerCapture(pointerId);
      document.body.style.cursor     = "col-resize";
      document.body.style.userSelect = "none";

      const onMove = () => {}; // no visual indicator — pointer capture handles cursor

      const onUp = (up) => {
        el.releasePointerCapture(pointerId);
        el.removeEventListener("pointermove",   onMove);
        el.removeEventListener("pointerup",     onUp);
        el.removeEventListener("pointercancel", onUp);

        document.body.style.cursor     = "";
        document.body.style.userSelect = "";

        const newSize = Math.max(30, startSize + (up.clientX - startX));
        setColumnSizing((prev) => ({ ...prev, [columnId]: newSize }));
      };

      el.addEventListener("pointermove",   onMove);
      el.addEventListener("pointerup",     onUp);
      el.addEventListener("pointercancel", onUp);
    };
    // ─────────────────────────────────────────────────────────────────────

    const handleExportToExcel = () => {
      const filteredRows  = table.getFilteredRowModel().rows;
      const dataColumns   = visibleColumns.filter((col) => col.accessorKey);
      const headers       = dataColumns.map((col) =>
        col.header && typeof col.header === "string" ? col.header : col.accessorKey
      );
      const rows = filteredRows.map((row) =>
        dataColumns.map((col) => row.getValue(col.accessorKey) ?? "")
      );
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      XLSX.writeFile(wb, `${exportFileName}.xlsx`);
    };

    // shared cell style — width:100% + minWidth:0 lets the CSS grid
    // actually clip the content instead of overflowing into the next column
    const cellStyle = {
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      padding: "0 8px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    };

    return (
      <div style={{ position: "relative" }}>

        <div
          ref={parentRef}
          style={{
            height: `${height}`,
            overflow: "auto",
            border: "2px solid #aaa",
            borderRadius: "8px",
            position: "relative",
          }}
        >
          {/* ── Header ── */}
          <div
            style={{
              display: "grid",
              width: `${totalTableWidth}px`,
              gridTemplateColumns,
              borderBottom: "2px solid #999",
              background: "rgb(219 219 219)",
              position: "sticky",
              top: 0,
              zIndex: 1,
              gap: "4px",
            }}
          >
            {table.getHeaderGroups().map((headerGroup) =>
              headerGroup.headers.map((header) => (
                <div
                  key={header.id}
                  style={{
                    width: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    overflow: "hidden",
                    padding: "4px 8px",
                    fontWeight: "bold",
                    position: "relative",
                    userSelect: "none",
                    cursor: header.column.getCanSort() ? "pointer" : "default",
                    borderRight: "1px solid #bbb",
                  }}
                  onClick={(e) => {
                    if (
                      e.target.tagName === "INPUT" ||
                      e.target.tagName === "BUTTON" ||
                      e.target.closest("input") ||
                      e.target.closest("button")
                    ) return;
                    header.column.getToggleSortingHandler()?.(e);
                  }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && (
                    <span>
                      {{ asc: " ▲", desc: " ▼" }[header.column.getIsSorted()] ?? ""}
                    </span>
                  )}

                  {/* 3-dot menu */}
                  {header.column.id !== "select" && (
                    <div style={{ position: "absolute", top: "4px", right: "4px" }}>
                      <ColumnMenu table={table} />
                    </div>
                  )}

                  {/* Filter input */}
                  {header.column.getCanFilter() && (
                    <div style={{ position: "relative", marginTop: "4px" }}>
                      <input
                        type="text"
                        placeholder="Filter..."
                        value={header.column.getFilterValue() ?? ""}
                        onChange={(e) => header.column.setFilterValue(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "4px 22px 4px 6px",
                          fontSize: "12px",
                          boxSizing: "border-box",
                          border: "1px solid #bbb",
                          borderRadius: "3px",
                        }}
                      />
                      {header.column.getFilterValue() && (
                        <button
                          onClick={() => header.column.setFilterValue("")}
                          style={{
                            position: "absolute",
                            right: "4px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            fontSize: "12px",
                            color: "#888",
                          }}
                        >
                          ❌
                        </button>
                      )}
                    </div>
                  )}

                  {/* Resize handle — pointer capture, no document listeners */}
                  {header.column.getCanResize() && (
                    <div
                      onPointerDown={(e) => handleResizePointerDown(e, header)}
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        height: "100%",
                        width: "5px",
                        cursor: "col-resize",
                        userSelect: "none",
                        touchAction: "none",
                        background: "transparent",
                        zIndex: 2,
                      }}
                    />
                  )}
                </div>
              ))
            )}
          </div>

          {/* ── Rows ── */}
          <div
            style={{
              height: rowVirtualizer.getTotalSize(),
              width: `${totalTableWidth}px`,
              position: "relative",
              textAlign: "center",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row    = table.getRowModel().rows[virtualRow.index];
              const isEven = virtualRow.index % 2 === 0;
              return (
                <div
                  key={row.id}
                  style={{
                    position: "absolute",
                    width: `${totalTableWidth}px`,
                    top: 0,
                    left: 0,
                    transform: `translateY(${virtualRow.start}px)`,
                    display: "grid",
                    gridTemplateColumns,
                    borderBottom: "1px solid #ccc",
                    padding: "7px 0",
                    background: isEven ? "#fafafa" : "#f1f1f1",
                    transition: "background 0.2s ease-in-out",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#dbeafe"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = isEven ? "#fafafa" : "#f1f1f1"; }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const colKey = cell.column.columnDef.accessorKey ?? cell.column.id;

                    if (colKey === "Call_Message" || colKey === "Sms_Message") {
                      return (
                        <SimpleTooltip key={cell.id} content={String(cell.getValue() ?? "")}>
                          <div style={cellStyle}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        </SimpleTooltip>
                      );
                    }

                    if (colKey === "NotificationCount") {
                      const count = Number(cell.getValue() ?? 0);
                      return (
                        <SimpleTooltip content="Click to view logs" key={cell.id}>
                          <div
                            onClick={() => {
                              if (count > 0) {
                                const tripRefId = row.original.TripNo;
                                if (tripRefId) onNotificationClick?.(tripRefId);
                              }
                            }}
                            style={{
                              ...cellStyle,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: count > 0 ? "pointer" : "not-allowed",
                              color: count > 0 ? "#0d6efd" : "#888",
                              textDecoration: count > 0 ? "underline" : "none",
                              opacity: count > 0 ? 1 : 0.5,
                              transition: "color 0.2s ease",
                            }}
                          >
                            <span style={{ marginRight: "4px" }}>📄</span>
                            {count}
                          </div>
                        </SimpleTooltip>
                      );
                    }

                    return (
                      <div key={cell.id} style={cellStyle}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            marginTop: "8px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {showExportButton && (
            <button
              onClick={handleExportToExcel}
              title="Export to Excel"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 12px",
                fontSize: "13px",
                fontWeight: "600",
                color: "#fff",
                background: "#1d6f42",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8.5 17l-2-2.5H8V12h1v2.5h1.5L8.5 17zm4 0-2-2.5H12V12h1v2.5h1.5L12.5 17zm4 0-2-2.5H16V12h1v2.5h1.5L16.5 17z"/>
              </svg>
              Export to Excel
            </button>
          )}
          {showRecordCount && (
            <div style={{ fontSize: "14px", fontWeight: "bold" }}>
              {(() => {
                const filtered = table.getFilteredRowModel().rows.length;
                const total    = data.length;
                return `Records: ${filtered} of ${total}`;
              })()}
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default VirtualizedTable;
